import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { ConfirmationNumber as PackageIcon } from '@mui/icons-material';
import useCustomerOnlinePayments from '../../hooks/useCustomerOnlinePayments';
import CustomerServicePackageCard from '../../components/customer-packages/CustomerServicePackageCard';
import CustomerPageHeader from '../../components/customer/CustomerPageHeader';
import CustomerPageLoading from '../../components/customer/CustomerPageLoading';
import CustomerPageSection from '../../components/customer/CustomerPageSection';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StripeEmbeddedCheckoutDialog from '../../components/common/StripeEmbeddedCheckoutDialog';
import {
  allowsInStore,
  allowsOnline,
  normalizeCustomerPaymentMode,
} from '../../constants/CustomerPaymentMode';
import {
  createServicePackageCheckoutSession,
  getCustomerServicePackages,
  getStoreServicePackages,
} from '../../services/customerServicePackageService';
import { isCustomerServicePackageUsable } from '../../services/servicePackageService';
import StoreService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { getServiceName } from '../../utils/serviceTranslations';
import { normalizeLanguage, DEFAULT_LANGUAGE } from '../../constants/Languages';
import { buildPackageServiceNames, resolvePackageExpiryDateKey } from '../../utils/servicePackageUtils';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import useConsentGate from '../../hooks/useConsentGate';
import { ConsentContextType } from '../../services/consentFormService';

const CHECKOUT_ERROR_KEYS = {
  CustomerServicePackageAlreadyActive: 'customer.packages.error.alreadyActive',
  ServicePackageInactive: 'customer.packages.error.unavailable',
  CustomerOnlinePaymentsDisabled: 'customer.packages.error.onlinePaymentsDisabled',
  ConsentFormRequired: 'consentForms.errors.required',
};

const StorePackagesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { storeId, storeOverview } = useStore();
  const { ensureConsents, consentDialog } = useConsentGate(storeId);

  const [packages, setPackages] = useState([]);
  const [ownedPackages, setOwnedPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [expiryWarningPackage, setExpiryWarningPackage] = useState(null);
  const [checkoutPackageId, setCheckoutPackageId] = useState(null);

  const lang = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const storeLanguage = normalizeLanguage(storeOverview?.storeLanguage) || DEFAULT_LANGUAGE;
  const currency = storeOverview?.currency || packages[0]?.currency || 'EUR';
  const packagePaymentMode = normalizeCustomerPaymentMode(storeOverview?.servicePackageCustomerPaymentMode);
  const packageAllowsInStore = allowsInStore(packagePaymentMode);
  // A store can take a given customer's payments in store only, whatever it offers everyone else.
  const customerOnlinePaymentsAllowed = useCustomerOnlinePayments(storeId);
  const packageAllowsOnline = customerOnlinePaymentsAllowed && allowsOnline(packagePaymentMode);
  const hasActiveServicePackages = Boolean(storeOverview?.hasActiveServicePackages);

  // One usable copy per package at a time: that is the one shown on its card.
  const usableOwnedPackageByPackageId = useMemo(() => (
    ownedPackages
      .filter(isCustomerServicePackageUsable)
      .reduce((accumulator, ownedPackage) => {
        accumulator[ownedPackage.servicePackageId] = ownedPackage;
        return accumulator;
      }, {})
  ), [ownedPackages]);

  const sortedPackages = useMemo(() => (
    [...packages].sort((left, right) => {
      const leftOwned = Boolean(usableOwnedPackageByPackageId[left.id]);
      const rightOwned = Boolean(usableOwnedPackageByPackageId[right.id]);
      if (leftOwned === rightOwned) return 0;
      return leftOwned ? -1 : 1;
    })
  ), [packages, usableOwnedPackageByPackageId]);

  useEffect(() => {
    if (storeOverview && !hasActiveServicePackages) {
      navigate(buildCustomerRoute(storeId, ''), { replace: true });
    }
  }, [storeOverview, hasActiveServicePackages, storeId, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [packagesResult, ownedResult, servicesResult] = await Promise.all([
        getStoreServicePackages(storeId),
        getCustomerServicePackages(storeId),
        StoreService.getServices(true).catch(() => []),
      ]);

      setPackages(packagesResult);
      setOwnedPackages(ownedResult);
      setServices(servicesResult || []);
    } catch (err) {
      console.error('Failed to load packages:', err);
      setError(t('customer.packages.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [storeId, t]);

  useEffect(() => {
    if (storeOverview && !hasActiveServicePackages) {
      return;
    }
    loadData();
  }, [loadData, storeOverview, hasActiveServicePackages]);

  useEffect(() => {
    // Present when a customer returns from an off-site payment step (e.g. Klarna or Scalapay).
    // Stripe returns them here for failures too, so this cannot claim success — the reloaded
    // packages show the real state once the payment webhook lands.
    if (!searchParams.get('session_id')) {
      return;
    }

    setFeedback({ severity: 'info', message: t('customer.packages.checkout.processing') });
    loadData();

    searchParams.delete('session_id');
    setSearchParams(searchParams, { replace: true });
  }, [loadData, searchParams, setSearchParams, t]);

  // Consent forms of the included services are accepted before paying, so a refund dispute
  // about the treatment can be answered with what the customer agreed to.
  const startCheckout = async (servicePackage) => {
    setFeedback(null);
    try {
      const consent = await ensureConsents({
        servicePackageId: servicePackage.id,
        contextType: ConsentContextType.ServicePackagePurchase,
        contextItemId: servicePackage.id,
      });
      if (consent.accepted) {
        setCheckoutPackageId(servicePackage.id);
      }
    } catch (consentError) {
      console.error('Failed to check consent forms:', consentError);
      setFeedback({ severity: 'error', message: t('consentForms.errors.load') });
    }
  };

  const handleBuy = (servicePackage) => {
    // The expiry is part of what is being bought, so it is spelled out before paying.
    if (servicePackage.expiresInMonths) {
      setExpiryWarningPackage(servicePackage);
      return;
    }
    startCheckout(servicePackage);
  };

  const handleExpiryWarningConfirm = () => {
    const servicePackage = expiryWarningPackage;
    setExpiryWarningPackage(null);
    if (servicePackage) {
      startCheckout(servicePackage);
    }
  };

  const handleCheckoutComplete = () => {
    setCheckoutPackageId(null);
    setFeedback({ severity: 'success', message: t('customer.packages.checkout.success') });
    loadData();
  };

  const createCheckoutSession = async () => {
    try {
      return await createServicePackageCheckoutSession(storeId, checkoutPackageId);
    } catch (err) {
      const errorKey = CHECKOUT_ERROR_KEYS[getApiErrorCode(err)];
      if (errorKey) {
        setCheckoutPackageId(null);
        setFeedback({ severity: 'error', message: t(errorKey) });
      }
      throw err;
    }
  };

  const handleBook = (ownedPackage) => {
    navigate(buildCustomerRoute(storeId, 'booking'), {
      state: { preSelectedServicePackageId: ownedPackage.id },
    });
  };

  const translateServiceName = (nameDictionary) => getServiceName(nameDictionary, lang, storeLanguage);
  const warningExpiryDateKey = resolvePackageExpiryDateKey(expiryWarningPackage?.expiresInMonths);

  if (storeOverview && !hasActiveServicePackages) {
    return null;
  }

  return (
    <Box>
      <CustomerPageHeader
        icon={PackageIcon}
        title={t('customer.packages.title')}
        subtitle={t('customer.packages.subtitle')}
      />

      {!packageAllowsOnline ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('customer.packages.browseOnly')}
        </Alert>
      ) : null}

      {feedback ? (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <CustomerPageLoading />
      ) : sortedPackages.length === 0 ? (
        <CustomerPageSection variant="empty" sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: CUSTOMER_COLORS.text, mb: 0.5 }}>
            {t('customer.packages.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customer.packages.empty.subtitle')}
          </Typography>
        </CustomerPageSection>
      ) : (
        <Grid container spacing={2}>
          {sortedPackages.map((servicePackage) => (
            <Grid item xs={12} sm={6} md={4} key={servicePackage.id}>
              <CustomerServicePackageCard
                servicePackage={servicePackage}
                ownedPackage={usableOwnedPackageByPackageId[servicePackage.id]}
                serviceNames={buildPackageServiceNames(servicePackage.serviceIds, services, translateServiceName)}
                currency={currency}
                canBuy={packageAllowsOnline}
                showInStoreOnlyMessage={!packageAllowsOnline && packageAllowsInStore}
                actionLoading={checkoutPackageId === servicePackage.id}
                onBuy={handleBuy}
                onBook={handleBook}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={Boolean(expiryWarningPackage)}
        onClose={() => setExpiryWarningPackage(null)}
        onConfirm={handleExpiryWarningConfirm}
        title={t('customer.packages.expiryWarning.title')}
        message={t('customer.packages.expiryWarning.message', {
          count: Number(expiryWarningPackage?.expiresInMonths) || 0,
          date: warningExpiryDateKey ? formatCalendarDate(warningExpiryDateKey) : '',
        })}
        confirmText={t('customer.packages.expiryWarning.confirm')}
        cancelText={t('common.cancel')}
        confirmColor="primary"
      />

      {consentDialog}

      <StripeEmbeddedCheckoutDialog
        open={Boolean(checkoutPackageId)}
        onClose={() => setCheckoutPackageId(null)}
        createSession={createCheckoutSession}
        onComplete={handleCheckoutComplete}
      />
    </Box>
  );
};

export default StorePackagesPage;
